import { faCircleQuestion } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  type HelpPlacement,
  useHelpPlacements,
} from '~/hooks/useHelpPlacements';
import { Title } from '~/styles/layoutElements';
import { HelpModal } from './HelpModal';

export interface TitleWithQuestionProps {
  title: string;
  className?: string;
  /**
   * The help placement this screen anchors: a stable English identifier, never
   * a translated heading. Omit it where legacy has no help for the heading, and
   * the `?` isn't rendered.
   */
  placement?: HelpPlacement;
}

/**
 * A screen heading and, where it has one, its contextual-help `?`.
 *
 * The icon is conditional, as in legacy: a screen with no anchor renders a bare
 * heading. The placement set counts as allowed while pending, so the icon never
 * waits on a round trip. See `useHelpPlacements`.
 */
export function TitleWithQuestion({
  title,
  className,
  placement,
}: TitleWithQuestionProps) {
  const { t } = useTranslation();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const placements = useHelpPlacements();

  const showIcon =
    placement !== undefined &&
    (placements === undefined || placements.has(placement));

  return (
    <>
      <TitleContainer className={className}>
        <Title>{title}</Title>
        {showIcon && (
          <QuestionIcon
            icon={faCircleQuestion}
            // A control, not decoration: a bare click handler on an `<svg>` is
            // unreachable by keyboard and screen reader.
            //
            // The ARIA button pattern rather than a `<button>` wrapper, because
            // e2e specs locate this icon as the direct following sibling of the
            // screen's `<h3>` and a wrapper breaks that xpath. FontAwesome
            // honours `role` and drops its default `aria-hidden` once a label
            // is present.
            role="button"
            tabIndex={0}
            // Legacy's `?` has no label; `wbs.contextual-help` is already
            // translated in all four locales.
            aria-label={t('wbs.contextual-help')}
            onClick={() => setIsHelpOpen(true)}
            onKeyDown={(event) => {
              // Both keys, because `role="button"` promises both.
              if (event.key === 'Enter' || event.key === ' ') {
                // Space would otherwise scroll the page.
                event.preventDefault();
                setIsHelpOpen(true);
              }
            }}
          />
        )}
      </TitleContainer>
      {showIcon && (
        <HelpModal
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          placement={placement}
        />
      )}
    </>
  );
}

const TitleContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
`;

const QuestionIcon = styled(FontAwesomeIcon)`
  color: var(--wbs-red);
  cursor: pointer;
  font-size: 20px;
  transition: opacity 0.15s ease;

  /* An <svg> gets no focus ring of its own; keyboard focus has to be visible. */
  &:focus-visible {
    outline: 2px solid var(--wbs-red);
    outline-offset: 2px;
  }
`;
